export const meta = {
  title: 'Terms of Service | WordCraftApp',
  description:
    'Terms for WordCraftApp download guides, online editor access, basic tutorials and comparisons with Microsoft Word.',
  updated_at: '2026-10-15',
};

## About WordCraftApp

WordCraftApp ("we", "us") operates wordcraftapp.com, an independent English-language guide to WordCraft. By using this website, you agree to these terms.

Our website provides:

- [Download guides](/download) with links to the developers' WordCraft releases and installation instructions.
- [Online access information](/online) and a [direct link to the browser editor](/app/index.html).
- [Basic operation tutorials](/tutorials) for getting started with documents, formatting, saving and exporting.
- A [WordCraft vs Microsoft Word comparison](/wordcraft-vs-microsoft-word) explaining differences, advantages and limitations.

These guides and access links are free to use and do not require a WordCraftApp account. WordCraft is developed by ArtCraft. We are not affiliated with, endorsed by or sponsored by ArtCraft or Microsoft.

## WordCraft software

WordCraft software has its own open-source licence terms: the MIT License or Apache License 2.0, at your option, along with applicable third-party notices. These software licences are separate from the terms for this guide website. See the [WordCraft repository](https://github.com/storytold/wordcraft), [MIT licence](/app/LICENSE-MIT) and [Apache licence](/app/LICENSE-APACHE) for details.

## Online editor and your documents

The hosted editor runs in your browser. You are responsible for the content you open or create, having permission to use it, and saving your work. WordCraftApp does not provide a document cloud-storage or backup service. Keep a separate copy of important documents and check formatting after moving files between editors.

Browser support, software features and file compatibility can change. Access to the hosted editor may be interrupted or updated, and a website account does not provide document synchronisation.

## Guides, tutorials and comparisons

We aim to make our English guides useful and accurate. Instructions and comparisons are informational and may depend on the release, operating system, browser or Microsoft Word edition you use. They do not guarantee that WordCraft will replace every Microsoft Word feature or preserve every document layout.

Check the developers' release notes, system requirements and licence information before installing or relying on a feature. If you find an outdated step or comparison, email [support@wordcraftapp.com](mailto:support@wordcraftapp.com) with the relevant page URL.

## Third-party links

Desktop download links lead to the developers' GitHub releases. Third-party websites, community services and software are operated by their respective providers, under their own terms and privacy policies. We do not control their availability or future changes.

## Trademarks

Microsoft Word, Microsoft, ArtCraft and WordCraft names and trademarks belong to their respective owners. We use these names to identify software, explain its use and make comparisons. Software licences do not transfer ownership of those trademarks to WordCraftApp or its visitors.

## Acceptable use

Use the website lawfully. Do not attempt unauthorised access, distribute malware through site features, disrupt the service or send automated requests at an abusive rate. If you use an optional account, protect your credentials and notify us of suspected unauthorised access.

## Availability and responsibility

The website and its guides are provided on an “as available” basis. To the extent permitted by applicable law, we do not guarantee uninterrupted access or that content is complete, current or suitable for a particular task. WordCraft's own licence terms govern the software's warranties and limitations.

To the extent permitted by applicable law, WordCraftApp is not liable for indirect or consequential losses arising from use of the website. Nothing in these terms excludes rights or liabilities that cannot lawfully be excluded.

## Privacy

Our [Privacy Policy](/privacy-policy) explains website data, browser storage, optional accounts and support correspondence.

## Changes and contact

We may update these terms as the website changes. The “Last updated” date identifies the current version.

For download-guide questions, broken links, tutorials, comparisons or access to the hosted editor, contact [support@wordcraftapp.com](mailto:support@wordcraftapp.com). We handle questions about WordCraftApp; software bug reports and development requests can be directed to the WordCraft developers through their repository.
